/**
 * «В ленту приехала новая вилка» — короткий сигнал и подсветка строки.
 *
 * Зачем вообще: сканер стоит открытым на втором мониторе, и человек в него не
 * смотрит непрерывно. Вилка живёт медианно семь секунд (поле `sv` в снапшоте с
 * прода) — пока он переведёт взгляд, её уже нет. Без сигнала преимущество в
 * скорости подачи, ради которого сделан дельта-протокол, теряется на последнем
 * метре: до глаза.
 *
 * Звук синтезируется, а не проигрывается файлом. Причина скучная: файл — это
 * лишний запрос, который может не доехать, и лишний путь, который может
 * протухнуть в кэше после выката. Осциллятор либо есть в браузере, либо нет.
 */

import type { Fork } from './protocol';
import { roiValue } from './protocol';

/** Сколько строка остаётся подсвеченной. Длиннее — лента мигает постоянно. */
export const FRESH_MS = 6_000;

/**
 * Правило «новой» вилки: её id мы видим впервые.
 *
 * Возраст здесь не проверяется. Проверять его пришлось бы против часов сервера,
 * а вилка, поднявшаяся из-под фильтра через минуту после появления, для этого
 * человека всё равно новая: он её ещё не видел.
 */
export function newcomers(forks: Iterable<Fork>, seen: ReadonlySet<string>): Fork[] {
  const fresh: Fork[] = [];
  for (const fork of forks) {
    if (!seen.has(fork.id)) fresh.push(fork);
  }
  return fresh;
}

/**
 * Стоит ли пищать: только если хотя бы одна из новых вилок дотягивает до порога.
 *
 * Порог обязателен. Сигнал на каждой вилке в 0.1% — это сигнал, который через
 * час выключают вместе со звуком вообще, и тогда пропущенной окажется та, ради
 * которой всё делалось.
 */
export const worthSound = (fresh: Fork[], minRoi: number): boolean =>
  fresh.some((fork) => roiValue(fork) >= minRoi);

type Ctor = new () => AudioContext;

let ctx: AudioContext | null = null;

/**
 * Короткий сигнал через Web Audio.
 *
 * Никогда не бросает. Звук — это удобство, а исключение из обработчика дельты
 * останавливает отрисовку ленты: цена ошибки здесь несоизмерима с пользой.
 * Браузер до первого клика по странице контекст не даст, и это нормально:
 * переключатель звука сам по себе является тем самым кликом.
 */
export function beep(volume = 0.4): void {
  try {
    const w = globalThis as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
    const Ctx = w.AudioContext ?? w.webkitAudioContext;
    if (!Ctx) return;
    ctx ??= new Ctx();
    if (ctx.state === 'suspended') void ctx.resume();

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;

    const now = ctx.currentTime;
    const peak = Math.max(0.0002, Math.min(1, volume) * 0.5);
    // Атака и затухание экспонентой: щелчок в наушниках на восьмом часу работы
    // раздражает сильнее, чем сам сигнал.
    gain.gain.setValueAtTime(0.0002, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0002, now + 0.18);
    osc.start(now);
    osc.stop(now + 0.2);
  } catch {
    // Нет Web Audio, автоплей запрещён, контекст закрыт — молча без звука.
  }
}
